// js/Views/Account/account-forms.js
/* =========================================================
   ACCOUNT FORMS
   Path: /wwwroot/js/Views/Account/account-forms.js
   
   Form submission handlers for login, register, forgot password,
   and reset password flows.
   
   Dependencies: 
   - /wwwroot/js/_shared/_utils.js (load first)
   - /wwwroot/js/Views/Account/account-validation.js (load second)
   - /wwwroot/js/Views/Account/account-ui.js (load third)
   
   Load Order: 4th
   
   OPTIMIZATION: Now passes both eventId AND slug to avoid server-side
   slug resolution when we already have it from the page.
   
   FIX v3: 
   - FormData must be created BEFORE lockForm() because lockForm
     disables all inputs, and disabled inputs are excluded from FormData.
   - Added Turnstile validation before form submission
   - Fixed empty-key error handling from server responses
   - Added try/finally blocks to ensure unlock always happens
   - Added defensive re-query of elements after unlock
   
   FIX v4:
   - Added Turnstile reset after ALL failed submissions
   - Turnstile tokens are single-use; must reset for retry attempts
   
   FIX v5:
   - Added role selection modal support for both login AND registration
   - Sponsors now get proper role selection when logging in or registering
   - RoleModal.init() now includes selectRoleUrl for server-side role assignment
   ========================================================= */

(function () {
    "use strict";


    // Get utilities from shared namespace
    var utils = window.AppUtils || {};
    var qs = utils.qs || window.qs;
    var getToken = utils.getToken;
    var isValidEmail = utils.isValidEmail;

    console.log('[account-forms] Utils loaded:', {
        hasAppUtils: !!window.AppUtils,
        hasQs: !!qs,
        hasGetToken: !!getToken,
        hasIsValidEmail: !!isValidEmail
    });

    // Get validation functions
    var validation = (window.AccountApp && window.AccountApp.validation) || {};
    var validatePassword = validation.validatePassword;
    var getFailedRequirements = validation.getFailedRequirements;
    var clearAllValidation = validation.clearAllValidation;
    var showFieldError = validation.showFieldError;
    var showFormErrors = validation.showFormErrors;

    console.log('[account-forms] Validation loaded:', {
        hasValidation: !!window.AccountApp?.validation,
        hasValidatePassword: !!validatePassword,
        hasClearAllValidation: !!clearAllValidation,
        hasShowFieldError: !!showFieldError
    });

    // Get UI functions
    var ui = (window.AccountApp && window.AccountApp.ui) || {};
    var getIsProcessing = ui.getIsProcessing;
    var setButtonState = ui.setButtonState;
    var lockForm = ui.lockForm;
    var forceUnlock = ui.forceUnlock;
    var showSection = ui.showSection || window.showSection;
    var setSmartFocus = ui.setSmartFocus;

    console.log('[account-forms] UI loaded:', {
        hasUi: !!window.AccountApp?.ui,
        hasGetIsProcessing: !!getIsProcessing,
        hasSetButtonState: !!setButtonState,
        hasLockForm: !!lockForm
    });

    /* =========================================================
       GA4 ANALYTICS HELPER
       ========================================================= */

    /**
     * Sends a GA4 event if gtag is loaded and a measurement ID is configured.
     * @param {string} eventName - GA4 event name
     * @param {Object} [params] - Event parameters
     */
    function _trackEvent(eventName, params) {
        var acct = window.ACCOUNT || {};
        if (typeof gtag !== 'function' || !acct.gaMeasurementId) return;
        var base = {
            event_name: acct.eventName,
            organization_name: acct.organizationName
        };
        gtag('event', eventName, Object.assign(base, params || {}));
    }

    // Ensure namespace exists
    window.AccountApp = window.AccountApp || {};

    // i18n strings (from window.i18n.strings with English fallbacks)
    var _s = (window.i18n && window.i18n.strings) || {};

    /* =========================================================
       Helper: Reset Turnstile Widget
       
       Resets the Turnstile widget to get a fresh token.
       Called after any failed form submission since tokens
       are single-use (consumed by server even on failure).
       ========================================================= */

    function resetTurnstileWidget() {
        if (window.resetTurnstile) {
            window.resetTurnstile();
        } else if (window.turnstile && window.TURNSTILE && window.TURNSTILE.widgetId) {
            // Fallback: use Turnstile API directly
            try {
                window.turnstile.reset(window.TURNSTILE.widgetId);
                // Clear our tracking state
                if (window.TURNSTILE) {
                    window.TURNSTILE.token = null;
                    window.TURNSTILE.isValid = false;
                    window.TURNSTILE.isExpired = false;
                }
            } catch (e) {
                console.warn('[resetTurnstileWidget] Reset failed:', e);
            }
        } else {
        }
    }

    /* =========================================================
       Helper: Turnstile Validation
       
       Checks if Turnstile is enabled and verified before submission.
       Returns { valid: true } or { valid: false, message: "..." }
       ========================================================= */

    function validateTurnstile() {
        // Check if Turnstile is enabled on this page
        if (!window.TURNSTILE || !window.TURNSTILE.enabled) {
            // Turnstile not enabled - allow submission
            return { valid: true };
        }

        // Check if we have a valid token
        if (window.TURNSTILE.isValid && window.TURNSTILE.token) {
            return { valid: true };
        }

        // Check if token expired
        if (window.TURNSTILE.isExpired) {
            return {
                valid: false,
                message: _s.accTurnstileExpired || "Security verification expired. Please verify again.",
                shouldReset: true
            };
        }

        // No valid token
        return {
            valid: false,
            message: _s.accTurnstilePleaseComplete || "Please complete the security verification.",
            shouldReset: false
        };
    }

    /**
     * Show Turnstile error and optionally reset the widget
     * @param {string} message - Error message to display
     * @param {Element} form - Form element for error display
     * @param {boolean} shouldReset - Whether to reset the Turnstile widget
     */
    function handleTurnstileError(message, form, shouldReset) {

        // Show error message
        if (showFormErrors) {
            showFormErrors(message, form);
        }

        // Highlight the Turnstile container
        var turnstileContainer = qs('#turnstile-container');
        if (turnstileContainer) {
            turnstileContainer.classList.add('turnstile-error');

            // Remove highlight after 3 seconds
            setTimeout(function () {
                turnstileContainer.classList.remove('turnstile-error');
            }, 3000);

            // Scroll Turnstile into view if not visible
            var rect = turnstileContainer.getBoundingClientRect();
            var isVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;
            if (!isVisible) {
                turnstileContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

        // Reset widget if needed
        if (shouldReset) {
            resetTurnstileWidget();
        }
    }

    /* =========================================================
       Helper: Get Event Context from Page
       
       Retrieves eventId and slug from the page configuration.
       This avoids server-side lookups when we already have the data.
       ========================================================= */

    function getEventContext() {
        // Try multiple sources for eventId and slug
        var eventId = null;
        var slug = null;

        // Source 1: Window config (set by Razor view)
        if (window.ACCOUNT) {
            eventId = window.ACCOUNT.eventId || null;
            slug = window.ACCOUNT.slug || null;
        }

        // Source 2: Hidden form inputs (fallback)
        if (!eventId) {
            var eventIdInput = qs('input[name="eventId"]');
            if (eventIdInput) {
                eventId = parseInt(eventIdInput.value, 10) || null;
            }
        }

        if (!slug) {
            var slugInput = qs('input[name="slug"]');
            if (slugInput) {
                slug = slugInput.value || null;
            }
        }

        // Source 3: URL path (last resort - slug is usually the path segment)
        if (!slug) {
            // URL pattern: /Account/Index/{slug} or /{slug}
            var pathParts = window.location.pathname.split('/').filter(Boolean);
            if (pathParts.length > 0) {
                // If path is /Account/Index/my-event, slug is 'my-event'
                // If path is /my-event, slug is 'my-event'
                var lastPart = pathParts[pathParts.length - 1];
                if (lastPart && lastPart !== 'Account' && lastPart !== 'Index') {
                    slug = lastPart;
                }
            }
        }

        // Source 4: ViewBag.slug from data attribute
        if (!slug) {
            var pageContainer = qs('[data-slug]');
            if (pageContainer) {
                slug = pageContainer.dataset.slug || null;
            }
        }


        return {
            eventId: eventId,
            slug: slug
        };
    }

    /* =========================================================
       Helper: Ensure Form Has Event Data
       
       Adds eventId and slug to FormData if not already present.
       ========================================================= */

    function ensureEventDataInForm(formData, form) {
        var context = getEventContext();

        // Check if eventId is already in form
        if (!formData.has('eventId') || !formData.get('eventId')) {
            if (context.eventId) {
                formData.set('eventId', context.eventId.toString());
            }
        }

        // Add slug to form (this is the optimization - server won't need to look it up)
        if (!formData.has('slug') || !formData.get('slug')) {
            if (context.slug) {
                formData.set('slug', context.slug);
            }
        }

        return context;
    }

    /* =========================================================
       Helper: Safe cleanup after form submission
       
       Ensures form is unlocked and button is reset even if errors occur
       ========================================================= */

    function safeCleanup(unlock, button, buttonState, focusElement) {
        try {
            // Always unlock first
            if (typeof unlock === 'function') {
                unlock();
            }
        } catch (e) {
            console.error('[safeCleanup] Error during unlock:', e);
            // Try force unlock as fallback
            if (forceUnlock) {
                try {
                    forceUnlock();
                } catch (e2) {
                    console.error('[safeCleanup] Force unlock also failed:', e2);
                }
            }
        }

        try {
            // Reset button state
            if (setButtonState && button) {
                setButtonState(button, buttonState || 'normal');
            }
        } catch (e) {
            console.error('[safeCleanup] Error resetting button:', e);
            // Manual fallback
            if (button) {
                button.disabled = false;
                button.classList.remove('checking');
            }
        }

        try {
            // Focus element if provided
            if (focusElement) {
                // Re-query element to ensure fresh reference
                var el = typeof focusElement === 'string' ? qs(focusElement) : focusElement;
                if (el && typeof el.focus === 'function') {
                    el.focus();
                }
            }
        } catch (e) {
            console.error('[safeCleanup] Error focusing element:', e);
        }
    }

    /* =========================================================
       Helper: Email-provider spam / delivery tip

       Returns a contextual help string based on the email domain.
       Gmail and Outlook/Hotmail users get specific guidance because
       IMAP clients (especially Outlook desktop) often hide the
       Spam/Junk folder, so the web client is the safest bet.
       ========================================================= */

    function getSpamTip(email) {
        if (!email) return null;

        var domain = (email.split('@')[1] || '').toLowerCase();

        if (domain === 'gmail.com' || domain === 'googlemail.com') {
            return {
                provider: _s.accGmailTipLabel || 'Gmail tip',
                tip: _s.accGmailTip || 'Sometimes Gmail tucks these into Spam. If you read Gmail through Outlook or another app, that folder might not show up\u2009\u2014\u2009try checking directly at mail.google.com.'
            };
        }

        if (domain === 'outlook.com' || domain === 'hotmail.com' ||
            domain === 'live.com'    || domain === 'msn.com') {
            return {
                provider: _s.accOutlookTipLabel || 'Outlook tip',
                tip: _s.accOutlookTip || 'It may have landed in your Junk folder. If you\u2019re using a desktop mail app, Junk doesn\u2019t always sync\u2009\u2014\u2009try checking at outlook.live.com.'
            };
        }

        if (domain === 'yahoo.com' || domain === 'ymail.com' ||
            domain === 'rocketmail.com') {
            return {
                provider: _s.accYahooTipLabel || 'Yahoo tip',
                tip: _s.accYahooTip || 'Yahoo can be a little overzealous with filtering. Take a peek in your Spam folder at mail.yahoo.com.'
            };
        }

        if (domain === 'icloud.com' || domain === 'me.com' ||
            domain === 'mac.com') {
            return {
                provider: _s.accICloudTipLabel || 'iCloud tip',
                tip: _s.accICloudTip || 'iCloud sometimes filters these out. Worth a quick look in your Junk folder at icloud.com/mail.'
            };
        }

        // Work / school / ISP — generic tip
        return {
            provider: _s.accTipLabel || 'Tip',
            tip: _s.accDefaultTip || 'If your email is managed by your workplace or school, spam filters can be strict. Check your junk or quarantine folder, or ask your IT team if it doesn\u2019t come through.'
        };
    }

    /**
     * Show the spam tip element based on email domain.
     * Populates the provider badge and tip text.
     * Works for both the AJAX path and server-rendered confirmation.
     * @param {string} email - The email address that was submitted
     */
    function updateSpamTip(email) {
        var tipContainer = qs('#spam-tip');
        var tipText = qs('#spam-tip-text');
        var tipBadge = qs('#spam-tip-provider');
        if (!tipContainer || !tipText) return;

        var result = getSpamTip(email);
        if (result) {
            if (tipBadge) tipBadge.textContent = result.provider;
            tipText.innerHTML = result.tip;
            tipContainer.style.display = 'flex';
        } else {
            tipContainer.style.display = 'none';
        }
    }

    /* =========================================================
       Helper: Handle Server Errors
       
       Processes error responses from server, handling:
       - Empty key errors (form-level)
       - Field-specific errors
       - String errors
       - Array errors
       - Turnstile failures
       
       IMPORTANT: Always resets Turnstile after server errors
       because the token was consumed even if login/register failed.
       
       Returns true if any error was displayed
       ========================================================= */

    function handleServerErrors(data, form) {
        var errorShown = false;

        // Special handling for explicit Turnstile failures from server
        if (data.turnstileFailed) {
            handleTurnstileError(_s.accTurnstileFailed || "Security verification failed. Please try again.", form, true);
            return true;
        }

        if (data.errors) {
            if (typeof data.errors === 'object' && !Array.isArray(data.errors)) {
                // Object of field errors: { "Login.Password": ["message"] } or { "": ["message"] }
                for (var key in data.errors) {
                    if (data.errors.hasOwnProperty(key)) {
                        var messages = Array.isArray(data.errors[key]) ? data.errors[key] : [data.errors[key]];

                        messages.forEach(function (msg) {
                            // Empty key or model-level key = form-level error
                            if (!key || key === "") {
                                if (showFormErrors) {
                                    showFormErrors(msg, form);
                                    errorShown = true;
                                }
                            } else {
                                if (showFieldError) {
                                    showFieldError(key, msg, form);
                                    errorShown = true;
                                }
                            }
                        });
                    }
                }
            } else if (typeof data.errors === 'string') {
                if (showFormErrors) {
                    showFormErrors(data.errors, form);
                    errorShown = true;
                }
            } else if (Array.isArray(data.errors)) {
                // Array of error messages
                data.errors.forEach(function (msg) {
                    if (showFormErrors) {
                        showFormErrors(msg, form);
                        errorShown = true;
                    }
                });
            }
        }

        // ═══════════════════════════════════════════════════════════════════
        // KEY FIX: Reset Turnstile after ANY server error
        // The token was consumed by the server, user needs a fresh one for retry
        // ═══════════════════════════════════════════════════════════════════
        resetTurnstileWidget();

        return errorShown;
    }

    /* =========================================================
       jQuery Validation Helper
       ========================================================= */

    function clearJQueryValidation(form) {
        if (!form) return;

        try {
            if (window.$ && $.fn.validate) {
                var $form = $(form);
                var validator = $form.data('validator');

                if (validator) {
                    validator.resetForm();

                    $form.find('.field-validation-error')
                        .removeClass('field-validation-error')
                        .addClass('field-validation-valid')
                        .empty();

                    $form.find('.input-validation-error')
                        .removeClass('input-validation-error')
                        .addClass('valid');

                    $form.find('.validation-summary-errors')
                        .removeClass('validation-summary-errors')
                        .addClass('validation-summary-valid')
                        .empty();
                }
            }
        } catch (e) {
            console.warn('[account-forms] clearJQueryValidation error:', e.message);
        }
    }

    /* =========================================================
       Helper: Show Role Selection Modal
       
       Initializes and shows the role selection modal with proper
       configuration for the SelectRole endpoint.
       ========================================================= */

    function showRoleSelectionModal(data, unlock, button) {

        // Unlock the form first (user needs to interact with modal)
        if (unlock) unlock();
        if (setButtonState && button) {
            setButtonState(button, 'normal');
        }

        // Initialize and show the role modal
        if (window.RoleModal) {
            window.RoleModal.init({
                selectRoleUrl: '/Account/SelectRole',
                sponsorRedirectUrl: data.sponsorRedirectUrl,
                advocateRedirectUrl: data.advocateRedirectUrl,
                advocateNeedsCreation: data.advocateNeedsCreation || false,
                eventId: data.eventId,
                slug: data.slug,
                advocateAlias: data.advocateAlias || 'Advocate',
                sponsorAlias: data.sponsorAlias || 'Event',
                contactAlias: data.contactAlias || 'Contact',
                beneficiaryAlias: data.beneficiaryAlias || 'Beneficiary'
            });
            window.RoleModal.show();
        } else {
            // Fallback: RoleModal not loaded - redirect to advocate path
            console.warn('[showRoleSelectionModal] RoleModal not loaded, falling back to redirect');
            var fallbackUrl = data.advocateRedirectUrl || data.redirectUrl || '/Advocate/Dashboard';
            window.location.href = fallbackUrl;
        }
    }

    /* =========================================================
       Login Handler
       ========================================================= */

    async function handleLogin(e) {
        e.preventDefault();
        e.stopPropagation();

        if (getIsProcessing && getIsProcessing()) {
            return false;
        }

        // USE e.target instead of qs("#login-form-el")
        var form = e.target;
        var formContainer = qs("#login-form");
        var button = qs(".btn-submit", formContainer);


        if (!form || form.tagName !== 'FORM') {
            console.error('[handleLogin] e.target is not a form!', form);
            return false;
        }

        // Clear validations - use FORM, not parent
        if (clearAllValidation) {
            clearAllValidation(form);
        }
        clearJQueryValidation(form);

        // ═══════════════════════════════════════════════════════════════════
        // TURNSTILE VALIDATION - Check BEFORE any other validation
        // ═══════════════════════════════════════════════════════════════════
        var turnstileCheck = validateTurnstile();
        if (!turnstileCheck.valid) {
            handleTurnstileError(turnstileCheck.message, form, turnstileCheck.shouldReset);
            return false;
        }

        var emailInput = qs('input[name="Login.Email"]', form);
        var passwordInput = qs('input[name="Login.Password"]', form);

        var email = (emailInput?.value || "").trim();
        var password = passwordInput?.value || "";

        // Get event context early for validation
        var eventContext = getEventContext();

        console.log('[handleLogin] Values:', {
            email: email,
            passwordLength: password.length,
            eventId: eventContext.eventId,
            slug: eventContext.slug
        });

        // Validate eventId
        if (!eventContext.eventId || eventContext.eventId === 0) {
            console.error('[handleLogin] Missing eventId!');
            if (showFormErrors) {
                showFormErrors(_s.accUnableDetermineEvent || "Unable to determine event. Please refresh the page.", form);
            }
            return false;
        }

        // Client-side validation
        if (!email || (isValidEmail && !isValidEmail(email))) {
            if (showFieldError) {
                showFieldError("Login.Email", _s.accInvalidEmail || "Please enter a valid email address.", form);
            }
            emailInput?.focus();
            return false;
        }

        if (!password) {
            if (showFieldError) {
                showFieldError("Login.Password", _s.accPasswordRequired || "Password is required.", form);
            }
            passwordInput?.focus();
            return false;
        }

        // IMPORTANT: Create FormData BEFORE locking the form!
        // lockForm() disables inputs, and disabled inputs are excluded from FormData
        var formData = new FormData(form);
        var token = getToken ? getToken(form) : '';

        // OPTIMIZATION: Ensure eventId and slug are in the form data
        ensureEventDataInForm(formData, form);

        var entries = [];
        formData.forEach(function (value, key) {
            entries.push(key + '=' + (key.toLowerCase().includes('password') ? '[REDACTED]' : value));
        });

        // NOW lock the form (after FormData is captured)
        var unlock = lockForm ? lockForm(formContainer) : function () { };
        if (setButtonState) {
            setButtonState(button, 'loading', _s.accSigningIn || 'Signing in…');
        }


        try {
            var response = await fetch(form.action, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'RequestVerificationToken': token || ''
                },
                redirect: 'manual'
            });


            var contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
                var data = await response.json();

                if (data.success === true) {
                    // ═══════════════════════════════════════════════════════════════════
                    // DUAL-ROLE SUPPORT: Check if server wants to show role selection modal
                    // This happens when user is a Sponsor for this event
                    // ═══════════════════════════════════════════════════════════════════
                    if (data.showRoleSelection) {
                        showRoleSelectionModal(data, unlock, button);
                        return false;
                    }

                    // Normal success - redirect to the provided URL
                    _trackEvent('login', { method: 'email' });
                    if (setButtonState) {
                        setButtonState(button, 'success', _s.accSuccess || 'Success!');
                    }
                    setTimeout(function () {
                        window.location.href = data.redirectUrl || '/Advocate/Dashboard';
                    }, 500);
                    return false;
                } else {
                    _trackEvent('login_error', { method: 'email' });

                    // Handle Turnstile-specific errors via centralized handler
                    if (data.turnstileFailed) {
                        handleServerErrors(data, form);
                    } else {
                        // For login failures, always use localized client-side message
                        // (server returns English-only error text)
                        if (showFormErrors) {
                            showFormErrors(_s.accInvalidOrPassword || "Invalid email or password. Please try again.", form);
                        }
                        resetTurnstileWidget();
                    }

                    // Safe cleanup with focus on password field
                    safeCleanup(unlock, button, 'normal', 'input[name="Login.Password"]');

                    // Clear password field (re-query to ensure fresh reference)
                    var pwdField = qs('input[name="Login.Password"]', form);
                    if (pwdField) {
                        pwdField.value = '';
                    }
                }
            } else if (response.type === 'opaqueredirect' || response.status === 0) {
                if (setButtonState) {
                    setButtonState(button, 'success', _s.accSuccess || 'Success!');
                }
                setTimeout(function () {
                    window.location.reload();
                }, 500);
                return false;
            } else {
                // Non-JSON response
                console.warn('[handleLogin] NON-JSON RESPONSE - showing generic error');
                if (showFormErrors) {
                    showFormErrors(_s.accInvalidOrPassword || "Invalid email or password. Please try again.", form);
                }

                // Reset Turnstile for retry (token was consumed by server)
                resetTurnstileWidget();

                // Safe cleanup with focus on password field
                safeCleanup(unlock, button, 'normal', 'input[name="Login.Password"]');

                // Clear password field
                var pwdField = qs('input[name="Login.Password"]', form);
                if (pwdField) {
                    pwdField.value = '';
                }
            }
        } catch (error) {
            console.error('[handleLogin] FETCH ERROR:', error);
            if (showFormErrors) {
                showFormErrors(_s.errorTryAgain || "An error occurred. Please try again.", form);
            }

            // Reset Turnstile for retry
            resetTurnstileWidget();

            // Safe cleanup
            safeCleanup(unlock, button, 'normal');
        }

        return false;
    }

    /* =========================================================
       Register Handler
       ========================================================= */

    async function handleRegister(e) {
        e.preventDefault();
        e.stopPropagation();

        if (getIsProcessing && getIsProcessing()) {
            return false;
        }

        var form = qs("#register-form-el");
        var formContainer = qs("#register-form");
        var button = qs("#register-form .btn-submit");

        if (!form) {
            console.error('[handleRegister] Form not found');
            return false;
        }

        // Clear validations - use FORM
        if (clearAllValidation) clearAllValidation(form);
        clearJQueryValidation(form);

        // ═══════════════════════════════════════════════════════════════════
        // TURNSTILE VALIDATION - Check BEFORE any other validation
        // ═══════════════════════════════════════════════════════════════════
        var turnstileCheck = validateTurnstile();
        if (!turnstileCheck.valid) {
            handleTurnstileError(turnstileCheck.message, form, turnstileCheck.shouldReset);
            return false;
        }

        var emailInput = qs('input[name="Register.Email"]', form);
        var displayNameInput = qs('input[name="Register.DisplayName"]', form);
        var passwordInput = qs('input[name="Register.Password"]', form);
        var confirmPasswordInput = qs('input[name="Register.ConfirmPassword"]', form);

        var email = (emailInput?.value || "").trim();
        var displayName = (displayNameInput?.value || "").trim();
        var password = passwordInput?.value || "";
        var confirmPassword = confirmPasswordInput?.value || "";

        // Get event context early for validation
        var eventContext = getEventContext();

        console.log('[handleRegister] Values:', {
            email: email,
            displayName: displayName,
            passwordLength: password.length,
            eventId: eventContext.eventId,
            slug: eventContext.slug
        });

        // Validate eventId
        if (!eventContext.eventId || eventContext.eventId === 0) {
            console.error('[handleRegister] Missing eventId!');
            if (showFormErrors) {
                showFormErrors(_s.accUnableDetermineEvent || "Unable to determine event. Please refresh the page.", form);
            }
            return false;
        }

        // Validations - use FORM
        if (!email || (isValidEmail && !isValidEmail(email))) {
            if (showFieldError) showFieldError("Register.Email", _s.accInvalidEmail || "Please enter a valid email address.", form);
            emailInput?.focus();
            return false;
        }

        if (!displayName) {
            if (showFieldError) showFieldError("Register.DisplayName", _s.accNameRequired || "Name is required.", form);
            displayNameInput?.focus();
            return false;
        }

        if (!password) {
            if (showFieldError) showFieldError("Register.Password", _s.accPasswordRequired || "Password is required.", form);
            passwordInput?.focus();
            return false;
        }

        if (validatePassword) {
            var passwordValidation = validatePassword(password);
            if (!passwordValidation.allPassed) {
                var failed = getFailedRequirements ? getFailedRequirements(password) : ['password requirements not met'];
                var pwdNeedsPrefix = _s.accPasswordNeeds || 'Password needs: ';
                var message = failed.length === 1
                    ? pwdNeedsPrefix + failed[0].toLowerCase()
                    : pwdNeedsPrefix + failed.map(function (f) { return f.toLowerCase(); }).join(', ');

                if (showFieldError) showFieldError("Register.Password", message, form);
                passwordInput?.focus();
                return false;
            }
        }

        if (!confirmPassword) {
            if (showFieldError) showFieldError("Register.ConfirmPassword", _s.accConfirmPasswordRequired || "Please confirm your password.", form);
            confirmPasswordInput?.focus();
            return false;
        }

        if (password !== confirmPassword) {
            if (showFieldError) showFieldError("Register.ConfirmPassword", _s.accPasswordsDoNotMatch || "Passwords do not match.", form);
            confirmPasswordInput?.focus();
            return false;
        }

        // IMPORTANT: Create FormData BEFORE locking the form!
        // lockForm() disables inputs, and disabled inputs are excluded from FormData
        var formData = new FormData(form);
        var token = getToken ? getToken(form) : '';

        // OPTIMIZATION: Ensure eventId and slug are in the form data
        ensureEventDataInForm(formData, form);

        // NOW lock the form (after FormData is captured)
        var unlock = lockForm ? lockForm(formContainer) : function () { };
        if (setButtonState) setButtonState(button, 'loading', _s.accCreatingAccount || 'Creating account…');

        try {
            var response = await fetch(form.action, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'RequestVerificationToken': token || ''
                },
                redirect: 'manual'
            });

            var contentType = response.headers.get('content-type');

            if (contentType && contentType.includes('application/json')) {
                var data = await response.json();

                if (data.success === true) {
                    // ═══════════════════════════════════════════════════════════════════
                    // DUAL-ROLE SUPPORT: Check if server wants to show role selection modal
                    // This happens when user registers with email matching existing sponsor
                    // ═══════════════════════════════════════════════════════════════════
                    if (data.showRoleSelection) {
                        
                        // Update button to show success before showing modal
                        if (setButtonState) {
                            setButtonState(button, 'success', _s.accAccountCreated || 'Account created!');
                        }
                        
                        // Short delay to show success, then show modal
                        setTimeout(function() {
                            showRoleSelectionModal(data, unlock, button);
                        }, 300);
                        
                        return false;
                    }

                    // Normal success - redirect to the provided URL
                    _trackEvent('sign_up', { method: 'email' });
                    if (setButtonState) setButtonState(button, 'success', _s.accAccountCreated || 'Account created!');
                    setTimeout(function () {
                        window.location.href = data.redirectUrl || '/Advocate/Onboarding';
                    }, 500);
                    return false;
                } else {
                    _trackEvent('sign_up_error', { method: 'email' });
                    // Handle errors using centralized handler (includes Turnstile reset)
                    var errorShown = handleServerErrors(data, form);

                    // Fallback if no error was shown
                    if (!errorShown && showFormErrors) {
                        showFormErrors(_s.accRegistrationFailed || "Registration failed. Please try again.", form);
                    }

                    safeCleanup(unlock, button, 'normal');
                }
            } else {
                console.warn('[handleRegister] Non-JSON response');
                if (showFormErrors) showFormErrors(_s.accRegistrationFailed || "Registration failed. Please try again.", form);

                // Reset Turnstile for retry
                resetTurnstileWidget();

                safeCleanup(unlock, button, 'normal');
            }
        } catch (error) {
            console.error('[handleRegister] Error:', error);
            if (showFormErrors) showFormErrors(_s.errorTryAgain || "An error occurred. Please try again.", form);

            // Reset Turnstile for retry
            resetTurnstileWidget();

            safeCleanup(unlock, button, 'normal');
        }

        return false;
    }

    /* =========================================================
       Forgot Password Handler
       ========================================================= */

    async function handleForgotPassword(e) {
        e.preventDefault();
        e.stopPropagation();

        if (getIsProcessing && getIsProcessing()) return false;

        var form = qs("#forgot-form-el");
        var formContainer = qs("#forgot-form");
        var button = qs("#forgot-form .btn-submit");
        var emailInput = qs("#forgot-email");

        if (!form) {
            console.error('[handleForgotPassword] Form not found');
            return false;
        }

        // Clear validations first
        if (clearAllValidation) clearAllValidation(form);
        clearJQueryValidation(form);

        // ═══════════════════════════════════════════════════════════════════
        // TURNSTILE VALIDATION - Check BEFORE any other validation
        // ═══════════════════════════════════════════════════════════════════
        var turnstileCheck = validateTurnstile();
        if (!turnstileCheck.valid) {
            handleTurnstileError(turnstileCheck.message, form, turnstileCheck.shouldReset);
            return false;
        }

        var email = (emailInput?.value || "").trim();
        if (!email || (isValidEmail && !isValidEmail(email))) {
            if (showFieldError) showFieldError("Email", _s.accInvalidEmail || "Please enter a valid email address", form);
            return false;
        }

        // IMPORTANT: Create FormData BEFORE locking the form!
        // lockForm() disables inputs, and disabled inputs are excluded from FormData
        var formData = new FormData(form);
        var token = getToken ? getToken(form) : '';

        // OPTIMIZATION: Ensure eventId and slug are in the form data
        ensureEventDataInForm(formData, form);

        // NOW lock the form (after FormData is captured)
        var unlock = lockForm ? lockForm(formContainer) : function () { };
        if (setButtonState) setButtonState(button, 'loading', _s.accSending || 'Sending…');

        try {
            var response = await fetch(form.action, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'RequestVerificationToken': token || ''
                }
            });

            // Check for Turnstile failure from server
            var contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                var data = await response.json();

                if (data.turnstileFailed) {
                    handleTurnstileError(_s.accTurnstileFailed || "Security verification failed. Please try again.", form, true);
                    safeCleanup(unlock, button, 'normal');
                    return false;
                }
            }

            // Always show success for security (don't reveal if user exists)
            if (setButtonState) setButtonState(button, 'success', _s.accEmailSent || 'Email sent!');

            var emailDisplay = qs("#forgot-confirmation .email-display");
            if (emailDisplay) emailDisplay.textContent = email;

            // Show email-provider-specific spam tip
            updateSpamTip(email);

            setTimeout(function () {
                safeCleanup(unlock, button, 'normal');
                if (showSection) showSection('forgot-confirmation');
            }, 500);

            sessionStorage.setItem('showingForgotConfirmation', 'true');
            sessionStorage.setItem('forgotEmail', email);

        } catch (error) {
            console.error('[handleForgotPassword] Error:', error);
            if (showFormErrors) showFormErrors(_s.accSendResetFailed || "Failed to send reset email. Please try again.", form);

            // Reset Turnstile for retry
            resetTurnstileWidget();

            safeCleanup(unlock, button, 'normal');
        }

        return false;
    }

    /* =========================================================
       Reset Password Handler
       ========================================================= */

    async function handleResetPassword(e) {
        e.preventDefault();
        e.stopPropagation();

        if (getIsProcessing && getIsProcessing()) return false;

        var form = e.target;
        var formContainer = qs("#reset-password-form");
        var button = qs(".btn-submit", form);

        var passwordInput = qs('input[name="Password"]', form);
        var confirmPasswordInput = qs('input[name="ConfirmPassword"]', form);

        var password = passwordInput?.value || "";
        var confirmPassword = confirmPasswordInput?.value || "";

        if (clearAllValidation) clearAllValidation(form);
        clearJQueryValidation(form);

        // ═══════════════════════════════════════════════════════════════════
        // TURNSTILE VALIDATION - Check BEFORE any other validation
        // ═══════════════════════════════════════════════════════════════════
        var turnstileCheck = validateTurnstile();
        if (!turnstileCheck.valid) {
            handleTurnstileError(turnstileCheck.message, form, turnstileCheck.shouldReset);
            return false;
        }

        if (!password) {
            if (showFieldError) showFieldError("Password", _s.accPasswordRequired || "Password is required.", form);
            passwordInput?.focus();
            return false;
        }

        if (validatePassword) {
            var passwordValidation = validatePassword(password);
            if (!passwordValidation.allPassed) {
                var failed = getFailedRequirements ? getFailedRequirements(password) : ['requirements not met'];
                var pwdNeedsPrefix = _s.accPasswordNeeds || 'Password needs: ';
                var message = failed.length === 1
                    ? pwdNeedsPrefix + failed[0].toLowerCase()
                    : pwdNeedsPrefix + failed.map(function (f) { return f.toLowerCase(); }).join(', ');

                if (showFieldError) showFieldError("Password", message, form);
                passwordInput?.focus();
                return false;
            }
        }

        if (password !== confirmPassword) {
            if (showFieldError) showFieldError("ConfirmPassword", _s.accPasswordsDoNotMatch || "Passwords do not match", form);
            confirmPasswordInput?.focus();
            return false;
        }

        // IMPORTANT: Create FormData BEFORE locking the form!
        // lockForm() disables inputs, and disabled inputs are excluded from FormData
        var formData = new FormData(form);
        var token = getToken ? getToken(form) : '';

        // OPTIMIZATION: Ensure slug is in the form data (for redirect after reset)
        ensureEventDataInForm(formData, form);

        // NOW lock the form (after FormData is captured)
        var unlock = lockForm ? lockForm(formContainer) : function () { };
        if (setButtonState) setButtonState(button, 'loading', _s.accResettingPassword || 'Resetting password…');

        try {
            var response = await fetch(form.action, {
                method: 'POST',
                body: formData,
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                    'RequestVerificationToken': token || ''
                }
            });

            var contentType = response.headers.get('content-type');

            if (response.redirected) {
                if (setButtonState) setButtonState(button, 'success', _s.accPasswordResetRedirecting || 'Password reset! Redirecting…');
                setTimeout(function () {
                    window.location.href = response.url;
                }, 500);
                return false;
            } else if (contentType && contentType.includes('application/json')) {
                var data = await response.json();

                // Check for Turnstile failure from server
                if (data.turnstileFailed) {
                    handleTurnstileError(_s.accTurnstileFailed || "Security verification failed. Please try again.", form, true);
                    safeCleanup(unlock, button, 'normal');
                    return false;
                }

                if (data.success) {
                    // ═══════════════════════════════════════════════════════════════════
                    // DUAL-ROLE SUPPORT: Check if server wants to show role selection modal
                    // This can happen after password reset if user is a sponsor
                    // ═══════════════════════════════════════════════════════════════════
                    if (data.showRoleSelection) {
                        if (setButtonState) setButtonState(button, 'success', _s.accPasswordReset || 'Password reset!');
                        setTimeout(function() {
                            showRoleSelectionModal(data, unlock, button);
                        }, 500);
                        return false;
                    }

                    if (data.autoLogin && data.redirectUrl) {
                        if (setButtonState) setButtonState(button, 'success', _s.accPasswordResetSigningIn || 'Password reset! Signing you in…');
                        setTimeout(function () {
                            window.location.href = data.redirectUrl;
                        }, 1000);
                        return false;
                    } else {
                        if (setButtonState) setButtonState(button, 'success', _s.accPasswordReset || 'Password reset!');

                        var loginEmail = qs('#login-form input[name="Login.Email"]');
                        var resetEmail = qs('input[name="Email"]', form)?.value;
                        if (loginEmail && resetEmail) loginEmail.value = resetEmail;

                        setTimeout(function () {
                            safeCleanup(unlock, button, 'normal');
                            if (showSection) showSection('login');

                            var banner = document.createElement('div');
                            banner.className = 'reset-success-banner';
                            banner.innerHTML =
                                '<svg class="success-check" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">' +
                                '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>' +
                                '<polyline points="22 4 12 14.01 9 11.01"></polyline>' +
                                '</svg>' +
                                '<div class="success-text">' +
                                '<strong>' + (_s.accPasswordResetSuccessful || 'Password reset successful!') + '</strong>' +
                                '<span>' + (_s.accCanNowSignIn || 'You can now sign in with your new password.') + '</span>' +
                                '</div>';

                            var loginForm = qs('#login-form');
                            if (loginForm && !qs('.reset-success-banner', loginForm)) {
                                loginForm.insertBefore(banner, loginForm.firstChild);
                            }

                            var url = new URL(window.location);
                            url.searchParams.delete('code');
                            url.searchParams.delete('email');
                            window.history.replaceState({}, '', url);

                            if (setSmartFocus) setSmartFocus('login');
                        }, 500);
                    }
                } else {
                    // -------------------------------------------------------
                    // Check for expired / invalid token specifically
                    // -------------------------------------------------------
                    var errStr = (typeof data.errors === 'string' ? data.errors : '').toLowerCase();
                    var isTokenError = errStr.indexOf('token') !== -1 ||
                                      errStr.indexOf('expired') !== -1 ||
                                      errStr.indexOf('invalid') !== -1;

                    if (isTokenError) {
                        // Show a prominent message with a link to request a new reset
                        var summaryDiv = qs('.validation-summary-errors', form);
                        if (!summaryDiv) {
                            // Create the summary container if it doesn't exist
                            summaryDiv = document.createElement('div');
                            summaryDiv.className = 'validation-summary-errors';
                            // Insert at the top of the form
                            form.insertBefore(summaryDiv, form.firstChild);
                        }
                        summaryDiv.innerHTML =
                            '<div style="text-align:center;">' +
                                '<p style="margin:0 0 0.5rem;font-weight:600;">' + (_s.accResetLinkInvalid || 'This reset link is no longer valid.') + '</p>' +
                                '<p style="margin:0 0 0.75rem;color:var(--text-muted);">' + (_s.accResetLinkExpiredDetail || 'Reset links can only be used once and expire after a short time.') + '</p>' +
                                '<a href="#" class="request-new-reset-link" ' +
                                   'style="color:var(--primary);font-weight:600;text-decoration:underline;">' +
                                   (_s.accRequestNewResetLink || 'Request a new reset link') +
                                '</a>' +
                            '</div>';
                        summaryDiv.style.display = 'block';

                        // Attach click handler (avoids inline onclick scoping issues)
                        var resetLink = summaryDiv.querySelector('.request-new-reset-link');
                        if (resetLink) {
                            resetLink.addEventListener('click', function (ev) {
                                ev.preventDefault();
                                // Clear reset params from URL so showSection's guard
                                // doesn't block leaving the reset-password section
                                var url = new URL(window.location);
                                url.searchParams.delete('code');
                                url.searchParams.delete('email');
                                window.history.replaceState({}, '', url);

                                var show = showSection || window.showSection ||
                                    (window.AccountApp && window.AccountApp.ui && window.AccountApp.ui.showSection);
                                if (show) show('forgot');
                            });
                        }

                        // Disable the submit button — no point retrying with a dead token
                        if (button) {
                            button.disabled = true;
                            button.classList.add('opacity-50');
                        }
                    } else {
                        // Handle other errors using centralized handler (includes Turnstile reset)
                        var errorShown = handleServerErrors(data, form);

                        // Fallback if no error was shown
                        if (!errorShown && showFormErrors) {
                            showFormErrors(_s.accResetLinkExpired || "Reset failed. The link may have expired.", form);
                        }
                    }

                    safeCleanup(unlock, button, 'normal');
                }
            } else {
                if (showFormErrors) showFormErrors(_s.accResetFailed || "Reset failed. Please request a new reset link.", form);

                // Reset Turnstile for retry
                resetTurnstileWidget();

                safeCleanup(unlock, button, 'normal');
            }
        } catch (error) {
            console.error('[handleResetPassword] Error:', error);
            if (showFormErrors) showFormErrors(_s.errorTryAgain || "An error occurred. Please try again.", form);

            // Reset Turnstile for retry
            resetTurnstileWidget();

            safeCleanup(unlock, button, 'normal');
        }

        return false;
    }

    /* =========================================================
       Email Gate Handler
       ========================================================= */

    async function routeFromEmailGate() {

        if (getIsProcessing && getIsProcessing()) return;

        var input = qs("#email-gate-input");
        var formContainer = qs("#email-gate");
        if (!input) return;

        var email = (input.value || "").trim();
        if (!email || (isValidEmail && !isValidEmail(email))) {
            if (showFieldError) showFieldError("Gate.Email", _s.accEnterValidEmail || "Enter a valid email address.", formContainer);
            return;
        }

        var btn = qs("#email-gate-continue");
        if (clearAllValidation) clearAllValidation(formContainer);

        // Note: Email gate doesn't require Turnstile (just checking if email exists)
        // But if you want to add it, uncomment below:
        /*
        var turnstileCheck = validateTurnstile();
        if (!turnstileCheck.valid) {
            handleTurnstileError(turnstileCheck.message, formContainer, turnstileCheck.shouldReset);
            return;
        }
        */

        // Note: Email gate doesn't use FormData, so lock order doesn't matter here
        var unlock = lockForm ? lockForm(formContainer) : function () { };
        if (setButtonState) setButtonState(btn, 'loading', _s.accChecking || 'Checking…');

        try {
            var token = getToken ? getToken(qs("#email-gate-form")) : '';
            var url = (window.ACCOUNT && window.ACCOUNT.checkEmailUrl) || "/Account/CheckEmailExists";
            var payload = JSON.stringify({
                email: email,
                eventId: (window.ACCOUNT && window.ACCOUNT.eventId) || 0
            });

            function postGate(csrf) {
                return fetch(url, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "RequestVerificationToken": csrf || ""
                    },
                    // AB#352: the event goes with the email so a closed event can route by sponsor
                    // rights. Omitted or zero, the server falls through to the normal login/register
                    // decision, so nothing changes for a live event.
                    body: payload
                });
            }

            var res = await postGate(token);

            // AB#466: a page left open long enough carries a stale antiforgery token, the POST comes
            // back 400, and the only handling was to throw - so the visitor was told we could not
            // verify their email. The server never looked the account up. It reads as "you are not
            // registered" to somebody who certainly is, and the thing that actually fixed it was
            // reloading the page, which is just a way of getting a fresh token.
            //
            // So get one and try again, once. Refreshing on their behalf is the whole fix.
            if (res.status === 400) {
                try {
                    var fresh = await fetch("/Account/AntiforgeryToken", {
                        method: "GET",
                        headers: { "Accept": "application/json" },
                        credentials: "same-origin"
                    });

                    if (fresh.ok) {
                        var freshData = await fresh.json();
                        if (freshData && freshData.token) {
                            res = await postGate(freshData.token);
                        }
                    }
                } catch (tokenErr) {
                    console.warn("[routeFromEmailGate] Could not refresh the antiforgery token:", tokenErr);
                }
            }

            if (!res.ok) throw new Error("HTTP " + res.status);
            var data = await res.json();

            // AB#352: honour the server's routing rather than re-deriving it from `exists`. The
            // server already sent nextForm and this ignored it, which was harmless while the only
            // two answers were login and register - it is not once a closed event can answer
            // "closed", because a non-sponsor with an account would have been sent to a login form
            // on a finished event.
            var exists = !!data.exists;
            var next = data.nextForm || (exists ? "login" : "register");

            var loginEmail = qs('#login-form input[name="Login.Email"]');
            var regEmail = qs('#register-form input[name="Register.Email"]');
            var forgotEmail = qs("#forgot-email");

            if (loginEmail) loginEmail.value = email;
            if (regEmail) regEmail.value = email;
            if (forgotEmail) forgotEmail.value = email;

            safeCleanup(unlock, btn, 'normal');
            if (showSection) showSection(next);

        } catch (err) {
            console.error("[routeFromEmailGate] Error:", err);
            if (showFieldError) showFieldError("Gate.Email", _s.accEmailVerifyFailed || "We couldn't verify that email. Please try again.", formContainer);
            safeCleanup(unlock, btn, 'normal');
        }
    }

    /* =========================================================
       Export to namespace
       ========================================================= */

    window.AccountApp.forms = {
        handleLogin: handleLogin,
        handleRegister: handleRegister,
        handleForgotPassword: handleForgotPassword,
        handleResetPassword: handleResetPassword,
        routeFromEmailGate: routeFromEmailGate,
        // Spam tip for forgot-confirmation (used by Razor init + AJAX path)
        updateSpamTip: updateSpamTip,
        // Expose helpers for testing/debugging
        getEventContext: getEventContext,
        validateTurnstile: validateTurnstile,
        resetTurnstileWidget: resetTurnstileWidget,
        // Role selection helper (can be called externally if needed)
        showRoleSelectionModal: showRoleSelectionModal
    };


})();
